import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { InputSwitch } from 'primereact/inputswitch';
import { Paginator } from 'primereact/paginator';
import useAxios from '../shared/hooks/useAxios';
import { useAuthStore } from '../auth/auth.store';
import './NotificationPopover.scss';
const typeFilters = [
    { label: 'All Types', value: 'all' },
    { label: 'Request', value: 'request' },
    { label: 'Review', value: 'review' },
    // Hidden for now — keep for future re-enable, do not remove.
    { label: 'Comment', value: 'comment', hidden: true },
    { label: 'AI', value: 'ai', hidden: true },
    { label: 'Alert', value: 'alert', hidden: true },
];
const POPOVER_PAGE_SIZE = 5;
const FULL_VIEW_PAGE_SIZE = 10;
const NOTIFICATION_ENDPOINTS = {
    list: 'notification-manager/get_all_notifications',
    archivedList: 'notification-manager/get_archived_notifications',
    markRead: 'notification-manager/mark_notification_as_read',
    markAllRead: 'notification-manager/mark_all_notifications_as_read',
    archive: 'notification-manager/archive_notification',
    restore: 'notification-manager/restore_notification',
    unreadCountSocket: 'notification-manager/ws/get_unread_notification_count',
};
function matchesTab(notification, tab) {
    if (tab === 'mentions')
        return notification.mention;
    if (tab === 'archived')
        return true;
    return !notification.archived;
}
function getInitials(name) {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (!parts.length)
        return 'NA';
    return `${parts[0][0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase();
}
function formatRelativeTime(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime()))
        return '';
    const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
    if (seconds < 60)
        return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60)
        return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24)
        return `${hours} hr ago`;
    const days = Math.floor(hours / 24);
    if (days === 1)
        return 'Yesterday';
    if (days < 7)
        return `${days} days ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function titleFromNotification(notification) {
    const rawTitle = notification.event_name || notification.notification_type || 'Notification';
    return rawTitle
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
}
function typeFromNotification(notification) {
    const category = (notification.category ?? '').toLowerCase();
    const notificationType = (notification.notification_type ?? '').toLowerCase();
    if (category.includes('ai') || notificationType.includes('ai'))
        return 'ai';
    if (category.includes('annotation') || category.includes('mention') || notificationType.includes('comment') || notificationType.includes('mention'))
        return 'comment';
    if (category.includes('due') || category.includes('sla') || notificationType.includes('deadline') || notification.priority === 'critical')
        return 'alert';
    if (category.includes('verdict') || notificationType.includes('review') || notificationType.includes('approved'))
        return 'review';
    return 'request';
}
function iconFromType(type) {
    if (type === 'ai')
        return 'pi-sparkles';
    if (type === 'comment')
        return 'pi-comment';
    if (type === 'alert')
        return 'pi-clock';
    if (type === 'review')
        return 'pi-file-check';
    return 'pi-briefcase';
}
function avatarClassFromPriority(priority) {
    if (priority === 'critical')
        return 'avatar-red';
    if (priority === 'high')
        return 'avatar-orange';
    if (priority === 'medium')
        return 'avatar-blue';
    if (priority === 'low')
        return 'avatar-green';
    return 'avatar-indigo';
}
function mapApiNotification(notification) {
    const type = typeFromNotification(notification);
    const submittedBy = notification.notification_message.match(/^(.+?)\s+submitted\b/i)?.[1];
    const person = submittedBy || notification.source_module || notification.action_initiator || 'System';
    return {
        id: notification.id,
        title: titleFromNotification(notification),
        message: notification.notification_message,
        person,
        initials: getInitials(person),
        time: formatRelativeTime(notification.created_at),
        tag: notification.source_module || notification.request_id || notification.material_id || undefined,
        type,
        unread: !notification.is_read,
        mention: notification.category === 'mentions' || notification.notification_type === 'mention',
        archived: Boolean(notification.archived),
        avatarClass: avatarClassFromPriority(notification.priority),
        icon: iconFromType(type),
        deepLink: notification.deep_link,
        raw: notification,
    };
}
function getBucket(messages, tab, type) {
    if (tab === 'archived')
        return messages?.all_notifications;
    if (type !== 'all')
        return messages?.[type];
    if (tab === 'mentions')
        return messages?.mentions;
    return messages?.all_notifications;
}
function categoryFromTab(tab) {
    if (tab === 'mentions')
        return 'mentions';
    return 'all';
}
function buildWebSocketUrl() {
    const baseUrl = process.env.API_BASE_URL ?? window.location.origin;
    const httpUrl = new URL(NOTIFICATION_ENDPOINTS.unreadCountSocket, baseUrl).toString();
    return httpUrl.replace(/^http/, 'ws');
}
function getNotificationRoute(notification) {
    const queryParams = new URLSearchParams();
    if (notification.raw.annotation_id)
        queryParams.set('annotationId', notification.raw.annotation_id);
    if (notification.raw.comment_id)
        queryParams.set('commentId', notification.raw.comment_id);
    const querySuffix = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const targetAssetId = notification.raw.material_id || notification.raw.asset_id;
    if (!notification.deepLink) {
        if (notification.raw.request_id && targetAssetId) {
            return `/annotate/annotation-studio/${notification.raw.request_id}/${targetAssetId}${querySuffix}`;
        }
        return notification.raw.request_id ? `/annotate/${notification.raw.request_id}/review${querySuffix}` : null;
    }
    try {
        // deepLink can be a full absolute URL (possibly on a different host than this app).
        // Only the pathname/search/hash are meaningful here — resolving an absolute URL
        // against window.location.origin (instead of parsing it as-is) mangles it into a
        // single bogus path segment, which is what caused broken/duplicated navigation.
        const isAbsolute = /^[a-z][a-z0-9+.-]*:\/\//i.test(notification.deepLink);
        const link = isAbsolute
            ? new URL(notification.deepLink)
            : new URL(notification.deepLink.startsWith('/') ? notification.deepLink : `/${notification.deepLink}`, window.location.origin);
        const pathname = link.pathname;
        const reviewMatch = pathname.match(/^(?:\/annotate)?\/([^/]+)\/(review|review-submission)\/?$/i);
        if (reviewMatch) {
            const reviewQuery = new URLSearchParams(link.search);
            queryParams.forEach((value, key) => reviewQuery.set(key, value));
            const nextQuery = reviewQuery.toString();
            return `/annotate/${reviewMatch[1]}/${reviewMatch[2]}${nextQuery ? `?${nextQuery}` : ''}${link.hash}`;
        }
        const studioMatch = pathname.match(/^(?:\/annotate)?\/(?:annotation-studio|requests?)\/([^/]+)\/(?:versions\/|materials\/|assets\/)?([^/]+)\/?$/i);
        if (studioMatch && studioMatch[2] !== 'review' && studioMatch[2] !== 'review-submission' && studioMatch[2] !== 'edit') {
            const studioQuery = new URLSearchParams(link.search);
            queryParams.forEach((value, key) => studioQuery.set(key, value));
            const nextQuery = studioQuery.toString();
            return `/annotate/annotation-studio/${studioMatch[1]}/${studioMatch[2]}${nextQuery ? `?${nextQuery}` : ''}${link.hash}`;
        }
        if (notification.raw.request_id && targetAssetId) {
            const fallbackQuery = new URLSearchParams(link.search);
            queryParams.forEach((value, key) => fallbackQuery.set(key, value));
            const nextQuery = fallbackQuery.toString();
            return `/annotate/annotation-studio/${notification.raw.request_id}/${targetAssetId}${nextQuery ? `?${nextQuery}` : ''}${link.hash}`;
        }
        const passthroughQuery = new URLSearchParams(link.search);
        queryParams.forEach((value, key) => passthroughQuery.set(key, value));
        const nextQuery = passthroughQuery.toString();
        return `${link.pathname}${nextQuery ? `?${nextQuery}` : ''}${link.hash}`;
    }
    catch {
        if (notification.raw.request_id && targetAssetId) {
            return `/annotate/annotation-studio/${notification.raw.request_id}/${targetAssetId}${querySuffix}`;
        }
        return notification.raw.request_id ? `/annotate/${notification.raw.request_id}/review${querySuffix}` : null;
    }
}
export default function NotificationPopover() {
    const navigate = useNavigate();
    const { get, post } = useAxios();
    const accessToken = useAuthStore((state) => state.accessToken);
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
    const currentUser = useAuthStore((state) => state.user);
    const [notifications, setNotifications] = useState([]);
    const [totalItems, setTotalItems] = useState(0);
    const [tabTotals, setTabTotals] = useState({ all: 0, mentions: 0, archived: 0 });
    const [globalUnreadCount, setGlobalUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [isFullViewOpen, setIsFullViewOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('all');
    const [activeType, setActiveType] = useState('all');
    const [onlyUnread, setOnlyUnread] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const containerRef = useRef(null);
    const reconnectTimerRef = useRef(null);
    const initialFetchDoneRef = useRef(false);
    const authReady = isAuthenticated && Boolean(currentUser?.id);
    const unreadCount = globalUnreadCount;
    useEffect(() => {
        function handleClickOutside(event) {
            if (!containerRef.current?.contains(event.target)) {
                setIsPopoverOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    useEffect(() => {
        if (!authReady || !accessToken)
            return undefined;
        let socket = null;
        let shouldReconnect = true;
        const connect = () => {
            const token = useAuthStore.getState().accessToken;
            if (!token)
                return;
            socket = new WebSocket(buildWebSocketUrl(), token);
            socket.onmessage = (event) => {
                try {
                    const payload = JSON.parse(event.data);
                    if (Array.isArray(payload?.errors) && payload.errors.length > 0)
                        return;
                    const nextCount = payload?.messages?.[0]?.unread_notification_count;
                    if (typeof nextCount === 'number')
                        setGlobalUnreadCount(nextCount);
                }
                catch {
                    // Ignore malformed socket payloads and wait for the next push.
                }
            };
            socket.onclose = () => {
                if (!shouldReconnect)
                    return;
                reconnectTimerRef.current = window.setTimeout(connect, 3000);
            };
        };
        connect();
        return () => {
            shouldReconnect = false;
            if (reconnectTimerRef.current)
                window.clearTimeout(reconnectTimerRef.current);
            socket?.close();
        };
    }, [accessToken, authReady]);
    const fetchUnreadCount = async () => {
        if (!authReady)
            return;
        try {
            const params = new URLSearchParams({
                results_per_page: '1',
                page_number: '1',
                category: 'all',
                unread_only: 'true',
                order_by: 'desc',
            });
            const response = await get(`${NOTIFICATION_ENDPOINTS.list}?${params.toString()}`, { skipSpinner: true });
            const errors = response?.data?.errors ?? [];
            if (errors.length > 0)
                throw new Error(errors[0]);
            const messages = response?.data?.messages;
            setGlobalUnreadCount(messages?.all_notifications?.total_items ?? messages?.total_items ?? 0);
        }
        catch {
            setGlobalUnreadCount(0);
        }
    };
    const fetchNotifications = async () => {
        if (!authReady)
            return;
        if (activeTab === 'mentions' || activeTab === 'archived') {
            // Mentions and Archived have no data source wired up yet — skip the API call
            // and just show the empty state until that's ready.
            setNotifications([]);
            setTotalItems(0);
            return;
        }
        setLoading(true);
        try {
            const params = new URLSearchParams({
                results_per_page: String(isFullViewOpen ? FULL_VIEW_PAGE_SIZE : POPOVER_PAGE_SIZE),
                page_number: String(isFullViewOpen ? currentPage : 1),
                order_by: 'desc',
            });
            params.set('category', activeType !== 'all' ? activeType : categoryFromTab(activeTab));
            if (onlyUnread)
                params.set('unread_only', 'true');
            const response = await get(`${NOTIFICATION_ENDPOINTS.list}?${params.toString()}`, { skipSpinner: true });
            const errors = response?.data?.errors ?? [];
            if (errors.length > 0)
                throw new Error(errors[0]);
            const messages = response?.data?.messages;
            const bucket = getBucket(messages, activeTab, activeType);
            const items = bucket?.notifications ?? [];
            const mapped = items
                .filter((notification) => !notification.deleted)
                .map(mapApiNotification);
            setNotifications(mapped);
            setTotalItems(bucket?.total_items ?? messages?.total_items ?? mapped.length);
            setTabTotals((current) => ({
                ...current,
                ...(messages?.all_notifications ? { all: messages.all_notifications.total_items } : {}),
                ...(messages?.mentions ? { mentions: messages.mentions.total_items } : {}),
            }));
            fetchUnreadCount();
        }
        catch {
            setNotifications([]);
            setTotalItems(0);
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (!authReady) {
            initialFetchDoneRef.current = false;
            return;
        }
        if (accessToken && !initialFetchDoneRef.current) {
            initialFetchDoneRef.current = true;
            fetchNotifications();
            fetchUnreadCount();
        }
    }, [accessToken, authReady]);
    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab, activeType, onlyUnread, isFullViewOpen]);
    useEffect(() => {
        if (isPopoverOpen || isFullViewOpen) {
            fetchNotifications();
        }
    }, [activeTab, activeType, currentPage, isFullViewOpen, isPopoverOpen, authReady, onlyUnread]);
    const filteredNotifications = useMemo(() => {
        return notifications.filter((notification) => {
            const tabMatch = matchesTab(notification, activeTab);
            const unreadMatch = !onlyUnread || notification.unread;
            // Non-archived tabs already get type-filtered server-side via the `category` param
            // (see getBucket); re-filtering here with the client-side heuristic can disagree
            // with the server's classification and wrongly drop matching results.
            const typeMatch = activeTab === 'archived' ? activeType === 'all' || notification.type === activeType : true;
            return tabMatch && unreadMatch && typeMatch;
        });
    }, [activeTab, activeType, notifications, onlyUnread]);
    const popoverNotifications = filteredNotifications.slice(0, POPOVER_PAGE_SIZE);
    const fullViewNotificationCount = activeTab === 'archived' ? filteredNotifications.length : totalItems;
    const showFullViewPaginator = isFullViewOpen && fullViewNotificationCount > FULL_VIEW_PAGE_SIZE;
    const markAsRead = async (notification) => {
        if (!notification.unread)
            return;
        setNotifications((current) => current.map((item) => item.id === notification.id ? { ...item, unread: false } : item));
        setGlobalUnreadCount((count) => Math.max(count - 1, 0));
        try {
            const response = await post(NOTIFICATION_ENDPOINTS.markRead, { notification_id: notification.id });
            const errors = response?.data?.errors ?? [];
            if (errors.length > 0)
                throw new Error(errors[0]);
        }
        catch {
            setNotifications((current) => current.map((item) => item.id === notification.id ? { ...item, unread: true } : item));
            setGlobalUnreadCount((count) => count + 1);
        }
        finally {
            fetchUnreadCount();
        }
    };
    const markAllAsRead = async () => {
        const previousNotifications = notifications;
        const previousGlobalUnreadCount = globalUnreadCount;
        setNotifications((current) => current.map((notification) => ({ ...notification, unread: false })));
        setGlobalUnreadCount(0);
        try {
            const response = await post(NOTIFICATION_ENDPOINTS.markAllRead);
            const errors = response?.data?.errors ?? [];
            if (errors.length > 0)
                throw new Error(errors[0]);
        }
        catch {
            setNotifications(previousNotifications);
            setGlobalUnreadCount(previousGlobalUnreadCount);
        }
        finally {
            fetchUnreadCount();
        }
    };
    const handleNotificationClick = async (notification) => {
        setIsPopoverOpen(false);
        setIsFullViewOpen(false);
        await markAsRead(notification);
        try {
            const route = getNotificationRoute(notification);
            if (route)
                navigate(route);
            else if (notification.raw.request_id) {
                navigate(`/annotate/${notification.raw.request_id}/review`);
            }
        }
        catch {
            if (notification.raw.request_id) {
                navigate(`/annotate/${notification.raw.request_id}/review`);
            }
        }
    };
    const openFullView = () => {
        setIsPopoverOpen(false);
        setIsFullViewOpen(true);
    };
    const renderTabs = () => (_jsxs("div", { className: "notification-tabs", role: "tablist", "aria-label": "Notification tabs", children: [_jsxs("button", { type: "button", className: activeTab === 'all' ? 'active' : '', onClick: () => setActiveTab('all'), children: ["All ", _jsx("span", { children: tabTotals.all })] }), _jsxs("button", { type: "button", className: activeTab === 'mentions' ? 'active' : '', onClick: () => setActiveTab('mentions'), children: ["Mentions ", _jsx("span", { children: tabTotals.mentions })] }), _jsxs("button", { type: "button", className: activeTab === 'archived' ? 'active' : '', onClick: () => setActiveTab('archived'), children: ["Archived ", _jsx("span", { children: tabTotals.archived })] })] }));
    const renderNotificationList = (items, isFullView = false) => (_jsx("div", { className: isFullView ? 'notification-list notification-list-full' : 'notification-list', children: loading ? (_jsxs("div", { className: "notification-empty", children: [_jsx("i", { className: "pi pi-spin pi-spinner" }), _jsx("span", { children: "Loading notifications" })] })) : items.length === 0 ? (_jsxs("div", { className: "notification-empty", children: [_jsx("i", { className: "pi pi-bell" }), _jsx("span", { children: "You are all caught up" })] })) : (items.map((notification) => (_jsxs("button", { type: "button", className: `notification-row ${notification.unread ? 'unread' : ''}`, onClick: () => handleNotificationClick(notification), children: [_jsxs("span", { className: `notification-avatar ${notification.avatarClass}`, children: [notification.initials, _jsx("i", { className: `pi ${notification.icon}` })] }), _jsxs("span", { className: "notification-copy", children: [_jsx("span", { className: "notification-title", children: notification.title }), _jsx("span", { className: "notification-message", children: notification.message })] }), _jsxs("span", { className: "notification-meta", children: [_jsx("span", { children: notification.time }), notification.unread && _jsx("span", { className: "notification-dot" })] })] }, notification.id)))) }));
    return (_jsxs("div", { className: "notification-shell", ref: containerRef, children: [_jsxs("button", { type: "button", className: "notification-trigger", onClick: () => setIsPopoverOpen((open) => !open), "aria-label": "Open notifications", "aria-expanded": isPopoverOpen, children: [_jsx("i", { className: "pi pi-bell" }), unreadCount > 0 && _jsx("span", { className: "notification-badge", children: unreadCount })] }), isPopoverOpen && (_jsxs("section", { className: "notification-popover", "aria-label": "Notifications", children: [_jsxs("div", { className: "notification-panel-header", children: [_jsxs("div", { className: "notification-heading", children: [_jsx("h2", { children: "Notifications" }), _jsx("span", { children: unreadCount })] }), _jsxs("div", { className: "notification-controls", children: [_jsx("label", { htmlFor: "notification-unread-toggle", children: "Only unread" }), _jsx(InputSwitch, { inputId: "notification-unread-toggle", checked: onlyUnread, onChange: (event) => setOnlyUnread(Boolean(event.value)) }), _jsx("button", { type: "button", className: "notification-icon-btn", onClick: markAllAsRead, "aria-label": "Mark all read", children: _jsx("i", { className: "pi pi-check" }) })] })] }), renderTabs(), renderNotificationList(popoverNotifications), _jsxs("div", { className: "notification-panel-footer", children: [_jsxs("span", { children: [totalItems, " total"] }), _jsxs("button", { type: "button", onClick: openFullView, children: ["View all ", _jsx("i", { className: "pi pi-chevron-right" })] })] })] })), isFullViewOpen && (_jsx("div", { className: "notification-modal-backdrop", role: "presentation", children: _jsxs("section", { className: "notification-modal", role: "dialog", "aria-modal": "true", "aria-label": "All notifications", children: [_jsxs("div", { className: "notification-panel-header notification-modal-header", children: [_jsxs("div", { className: "notification-heading", children: [_jsx("i", { className: "pi pi-bell" }), _jsx("h2", { children: "Notifications" }), _jsx("span", { children: unreadCount })] }), _jsxs("div", { className: "notification-modal-actions", children: [_jsx("label", { htmlFor: "notification-modal-unread-toggle", children: "Only unread" }), _jsx(InputSwitch, { inputId: "notification-modal-unread-toggle", checked: onlyUnread, onChange: (event) => setOnlyUnread(Boolean(event.value)) }), _jsxs("button", { type: "button", onClick: markAllAsRead, children: [_jsx("i", { className: "pi pi-check" }), " Mark all read"] }), _jsx("button", { type: "button", className: "notification-icon-btn", onClick: () => setIsFullViewOpen(false), "aria-label": "Close", children: _jsx("i", { className: "pi pi-times" }) })] })] }), renderTabs(), _jsxs("div", { className: "notification-type-filters", "aria-label": "Notification type filters", children: [_jsx("i", { className: "pi pi-filter" }), typeFilters.filter((filter) => !filter.hidden).map((filter) => (_jsx("button", { type: "button", className: activeType === filter.value ? 'active' : '', onClick: () => setActiveType(filter.value), children: filter.label }, filter.value)))] }), renderNotificationList(filteredNotifications, true), showFullViewPaginator && (_jsx("div", { className: "notification-paginator", children: _jsx(Paginator, { first: (currentPage - 1) * FULL_VIEW_PAGE_SIZE, rows: FULL_VIEW_PAGE_SIZE, totalRecords: totalItems, onPageChange: (event) => setCurrentPage((event.page ?? 0) + 1), template: "PrevPageLink PageLinks NextPageLink" }) })), _jsxs("div", { className: "notification-panel-footer", children: [_jsxs("span", { children: [fullViewNotificationCount, " notifications"] }), _jsx("button", { type: "button", onClick: () => setIsFullViewOpen(false), children: "Close" })] })] }) }))] }));
}
